import type { TUser } from '@shared/types/user';
import type { FC } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ExpandMore } from '@mui/icons-material';
import { Box } from '@mui/material';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@src/design-system/collapsible';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';
import { RefreshRightSquare } from 'iconsax-react';
import { useEditResetPassword } from '../../services/reset-password';

const RegistrationInfo: FC<{ user: TUser }> = ({ user }) => {
  const [open, setOpen] = useState(true);
  const closeRef = useRef<HTMLButtonElement>(null);
  const { mutateAsync, isPending } = useEditResetPassword();

  const handleResetPassword = async () => {
    await mutateAsync({ email: user.email });
    closeRef.current?.click();
  };

  return (
    <Box
      className='w-full border border-primary-200 rounded-lg py-3 px-3 mt-4'
      sx={{ bgcolor: '#fdfdfe' }}
    >
      <Collapsible
        open={open}
        onOpenChange={setOpen}
      >
        <CollapsibleTrigger asChild>
          <button className='flex items-center justify-between w-full text-lg font-semibold'>
            <p className='text-gray-800 text-base font-medium flex items-center gap-1'>
              Registration Information
            </p>
            <motion.div
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ExpandMore className='w-6 h-6' />
            </motion.div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent className='overflow-hidden mt-4'>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: open ? 1 : 0, height: open ? 'auto' : 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className='text-gray-600 text-sm mb-1'>Full Name</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {user.first_name + ' ' + user.last_name}
            </p>

            <p className='text-gray-600 text-sm mb-1'>Email</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {user.email}
            </p>

            <p className='text-gray-600 text-sm mb-1'>Phone Number</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {user.phone_number || '-'}
            </p>

            <p className='text-gray-600 text-sm mb-1'>Whatsapp Number</p>
            <p className='text-gray-800 font-medium text-sm mb-3'>
              {user.whatsapp_number || '-'}
            </p>

            <p className='text-gray-600 text-sm mb-1'>Password</p>
            <Dialog>
              <DialogTrigger
                asChild
                className='justify-start'
              >
                <p className='font-medium text-sm cursor-pointer text-primary-700 underline'>
                  Reset Password
                </p>
              </DialogTrigger>

              <DialogContent align='center'>
                <DialogHeader className='bg-white' />
                <DialogBody className='flex justify-center items-center flex-col'>
                  <span className='flex justify-center items-center rounded-2xl bg-primary-100 p-2 mb-4'>
                    <RefreshRightSquare
                      className='text-primary-1000'
                      variant='TwoTone'
                      size={35}
                    />
                  </span>
                  <p className='text-center text-gray-700 whitespace-pre-line'>
                    Are you Sure to reset the password for this user?
                  </p>
                </DialogBody>
                <DialogFooter className='sm:justify-center pt-0'>
                  <DialogClose ref={closeRef}>
                    <Button
                      variant='primaryOutline'
                      className='text-gray-800 border-primary-200'
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button
                    isLoading={isPending}
                    disabled={isPending}
                    variant='primary'
                    onClick={handleResetPassword}
                  >
                    Confirm
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </motion.div>
        </CollapsibleContent>
      </Collapsible>
    </Box>
  );
};

export default RegistrationInfo;
